/**
 * Content-Design-authored copy for the /redeem route (TG-43, Content
 * Design Agent mandatory per the constitution's trigger table for
 * user-facing text). Three states render in place on this one route,
 * per TG-44's own acceptance criterion ("RedeemPage renders throttled and
 * could-not-complete views in place"):
 *
 *   - `interstitial` — design state 1.3, shown on GET before Continue is
 *     pressed (AC-23).
 *   - `couldNotComplete` — design state 1.6, shown only when the confirm
 *     POST returns a 5xx status or no response (AC-25).
 *   - `throttled` — shown when the confirm POST returns status 429 (AC-8,
 *     AC-26), inside `PortalLayout` with the help line
 *     (ADR-20261008-hsvr amendment 2026-10-11).
 *   - `invalidFormat` — design state 1.5 (AC-8, ADR-20261016-f8ws), shown
 *     in place of the interstitial when the token extracted from the URL
 *     fails the client-side format check (fragment.ts's
 *     `isValidTokenFormat`) — never after a POST, and never a server
 *     outcome: the confirm endpoint is never called for this state.
 *
 * `interstitial` and `couldNotComplete` are copied byte for byte from
 * AC-23 and AC-25 respectively. `invalidFormat`'s heading and action label
 * are copied byte for byte from AC-8 ("We can't find that renewal link",
 * "Get a new link"); its body has no spec-dictated wording, so it is
 * Content-Design-authored, matching H2's plain, reassuring tone and
 * stating no cause this client cannot actually know (no "expired", no
 * "spent" — AC-8 reserves those for state 1.4). `throttled` has no
 * spec-dictated wording either —
 * AC-26 states only the required elements (a heading, one sentence telling
 * the customer to wait and try again later, and the negative list below),
 * so this text is Content-Design-authored to satisfy exactly those
 * elements, not transcribed from anywhere.
 *
 * `interstitial.helpLine` is the AC-29 help line shown on views for states
 * 1.1 to 1.4. The could-not-complete view (1.6) carries no help line,
 * no button, no retry action, no broker contact route and no helpdesk
 * contact route (AC-25, AC-29) — `couldNotComplete` below has no
 * `helpLine`, `actionLabel` or any action field, and none should be added.
 * The throttled view carries no token, no policy number, no renewal date,
 * no account name and no statement of attempt count (AC-26), and no action
 * field. Its help line is the same AC-29 string, which names none of those.
 */
export const redeemContent = {
  interstitial: {
    heading: "Finish signing in",
    body: "Select continue to finish signing in to your renewal. We will never ask you for a password or payment details.",
    serviceName: "Renewal portal",
    continueActionLabel: "Continue to your renewal",
    helpLine: "Need help? Contact your Account Manager.",
  },

  couldNotComplete: {
    heading: "We could not complete that",
    body: "Something went wrong on our side. Please try again later by opening the link in your renewal email.",
  },

  throttled: {
    heading: "Try again later",
    body: "Please wait a few minutes, then try again.",
    helpLine: "Need help? Contact your Account Manager.",
  },

  invalidFormat: {
    heading: "We can’t find that renewal link",
    body: "This link may have been typed or copied incorrectly.",
    newLinkActionLabel: "Get a new link",
    helpLine: "Need help? Contact your Account Manager.",
  },
} as const;
